import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, Modal, Select, Tabs, Tag, Tooltip, Typography, Upload, message } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  InfoCircleOutlined,
  MailOutlined,
  TagsOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { logEvent } from 'firebase/analytics';
import AppPageHeader from './components/AppPageHeader';
import { analytics } from './firebaseConfig';
import {
  getActiveProviderCategories,
  getProviderCategoryBySlug,
  getProviderCategoryRoute,
  getProvidersForCategory,
  providerBadgeDefinitions,
  RESOURCES_ROUTE,
  type ProviderBadge,
  type ServiceProvider,
  type ServiceProviderCategory,
} from './serviceProviderDirectory';
import {
  hasSeenResourceCategory,
  markResourceCategoriesSeen,
  markResourceCategorySeen,
  markResourcesSeen,
} from './resourcesNewness';
import { resolveFrontendApiBase } from './utils/apiBase';
import './Resources.css';
import { usePageMetadata } from './hooks/usePageMetadata';
import { PublishingGuidesSection } from './PublishingGuides';

const { Text } = Typography;
const API_BASE = resolveFrontendApiBase();

const RESOURCES_META_TITLE = 'Resources | ScribeShadow';
const RESOURCES_META_DESCRIPTION =
  'Explore practical publishing guides for translated editions and directories of independent author service providers.';
const EMAIL_SUBJECT = 'ScribeShadow Provider Directory Inquiry';
const PROVIDER_LOGO_MAX_BYTES = 5 * 1024 * 1024;
const PROVIDER_LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];

type ProviderDirectoryLoadStatus = 'loading' | 'success' | 'error';

type ProviderDirectoryLoadState = {
  status: ProviderDirectoryLoadStatus;
  providers: ServiceProvider[];
};

const useProviderDirectoryProviders = () => {
  const [loadState, setLoadState] = useState<ProviderDirectoryLoadState>({
    status: 'loading',
    providers: [],
  });
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    setLoadState({ status: 'loading', providers: [] });

    const loadProviders = async () => {
      try {
        const response = await fetch(`${API_BASE}/provider-directory/providers`, {
          method: 'GET',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Provider directory request failed with ${response.status}`);
        }

        const payload: unknown = await response.json();
        const providers = payload && typeof payload === 'object'
          ? (payload as { providers?: unknown }).providers
          : undefined;
        if (!Array.isArray(providers)) {
          throw new Error('Provider directory response was invalid.');
        }

        if (isActive) {
          setLoadState({ status: 'success', providers: providers as ServiceProvider[] });
        }
      } catch (error) {
        if (!isActive || (error instanceof Error && error.name === 'AbortError')) {
          return;
        }
        setLoadState({ status: 'error', providers: [] });
      }
    };

    void loadProviders();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [requestVersion]);

  const retry = useCallback(() => {
    setRequestVersion((version) => version + 1);
  }, []);

  return { ...loadState, retry };
};

const ProviderDirectoryStateCard: React.FC<{
  state: 'loading' | 'error' | 'empty';
  scope: 'hub' | 'category';
  onRetry?: () => void;
}> = ({ state, scope, onRetry }) => {
  if (state === 'loading') {
    return (
      <section className="resources-section-card resources-directory-note" role="status" aria-live="polite">
        <h2>Loading provider directory</h2>
        <p>Loading the latest provider information.</p>
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section className="resources-section-card resources-directory-note" role="alert">
        <h2>Provider directory unavailable</h2>
        <p>We could not load provider information. Please try again.</p>
        <Button type="primary" onClick={onRetry}>Retry</Button>
      </section>
    );
  }

  return (
    <section className="resources-section-card resources-directory-note" role="status" aria-live="polite">
      <h2>{scope === 'hub' ? 'No provider directories are available yet' : 'No providers are currently listed'}</h2>
      <p>
        {scope === 'hub'
          ? 'Provider directories will appear here when listings are published.'
          : 'There are no published providers in this directory right now. Please check back later.'}
      </p>
    </section>
  );
};


const trackDirectoryEvent = (
  eventName: string,
  category?: ServiceProviderCategory,
  provider?: ServiceProvider,
  extras?: Record<string, unknown>,
) => {
  if (!analytics) {
    return;
  }
  logEvent(analytics, eventName, {
    ...(category ? { categoryId: category.id, categoryTitle: category.title } : {}),
    ...(provider ? { providerId: provider.id, providerName: provider.name, badges: provider.badges, badgeList: provider.badges } : {}),
    ...(extras || {}),
  });
};

// TODO: route provider contact links through referral redirects if that infrastructure is added.
const getEmailHref = (email: string) =>
  `mailto:${email}?subject=${encodeURIComponent(EMAIL_SUBJECT)}`;

const shuffleProviders = (providers: ServiceProvider[]): ServiceProvider[] => {
  const shuffled = [...providers];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled;
};

type ProviderApplicationFormValues = {
  businessName: string;
  websiteUrl?: string;
  contactName: string;
  contactEmail: string;
  serviceTypes: string[];
  otherService?: string;
  services?: string;
  languages?: string;
  pricing?: string;
  turnaround?: string;
  workflowExperience?: string;
  requestedBadges?: ProviderBadge[];
  discountDetails?: string;
  referralDetails?: string;
  educationDetails?: string;
  notes?: string;
};

const providerApplicationServiceOptions = [
  { value: 'proofreading', label: 'Proofreading' },
  { value: 'editing', label: 'Editing' },
  { value: 'translation-review', label: 'Translation review' },
  { value: 'foreign-language-editing', label: 'Foreign-language editing' },
  { value: 'ai-assisted-translation-review', label: 'AI-assisted translation review' },
  { value: 'language-service-support', label: 'Language-service support' },
  { value: 'author-marketing', label: 'Author marketing' },
  { value: 'social-media-management', label: 'Social media management' },
  { value: 'ebook-formatting', label: 'Ebook formatting' },
  { value: 'print-formatting', label: 'Print formatting' },
  { value: 'author-pa-services', label: 'Author PA services' },
  { value: 'audiobook-production-coordination', label: 'Audiobook production coordination' },
  { value: 'storefront-setup', label: 'Storefront setup' },
  { value: 'other', label: 'Other' },
];

const providerApplicationBadgeOptions: Array<{ value: ProviderBadge; label: string }> = [
  { value: 'scribeshadow-workflows', label: providerBadgeDefinitions['scribeshadow-workflows'].label },
  { value: 'scribeshadow-user-discount', label: providerBadgeDefinitions['scribeshadow-user-discount'].label },
  { value: 'referral-participant', label: providerBadgeDefinitions['referral-participant'].label },
  { value: 'education-contributor', label: providerBadgeDefinitions['education-contributor'].label },
];

const getProviderApplicationServiceLabels = (serviceTypes?: string[]) => {
  const optionLabels = new Map(providerApplicationServiceOptions.map((option) => [option.value, option.label]));
  return (serviceTypes || []).map((serviceType) => optionLabels.get(serviceType) || serviceType);
};

const getProviderApplicationBadgeLabels = (requestedBadges?: ProviderBadge[]) => {
  const optionLabels = new Map(providerApplicationBadgeOptions.map((option) => [option.value, option.label]));
  return (requestedBadges || []).map((badge) => optionLabels.get(badge) || badge);
};

const appendProviderApplicationValue = (formData: FormData, key: string, value?: string) => {
  const normalized = value?.trim();
  if (normalized) {
    formData.append(key, normalized);
  }
};

const appendProviderApplicationValues = (formData: FormData, key: string, values?: string[]) => {
  (values || []).forEach((value) => appendProviderApplicationValue(formData, key, value));
};

const ProviderApplicationCard: React.FC<{
  category?: ServiceProviderCategory;
}> = ({ category }) => {
  const [form] = Form.useForm<ProviderApplicationFormValues>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<RcFile | null>(null);
  const [logoFileList, setLogoFileList] = useState<UploadFile[]>([]);
  const selectedServiceTypes = Form.useWatch('serviceTypes', form) || [];
  const selectedBadges = Form.useWatch('requestedBadges', form) || [];
  const showOtherService = selectedServiceTypes.includes('other');
  const showLanguageFields = selectedServiceTypes.some((serviceType) => [
    'translation-review',
    'foreign-language-editing',
    'ai-assisted-translation-review',
    'language-service-support',
  ].includes(serviceType));
  const showWorkflowField =
    selectedServiceTypes.includes('ai-assisted-translation-review') ||
    selectedBadges.includes('scribeshadow-workflows');
  const showDiscountField = selectedBadges.includes('scribeshadow-user-discount');
  const showReferralField = selectedBadges.includes('referral-participant');
  const showEducationField = selectedBadges.includes('education-contributor');
  const applicationSummary = category?.id === 'author-marketing'
    ? 'Independent author marketing, social media, storefront, or publishing support providers can request directory review.'
    : category?.id === 'publishing-support'
      ? 'Independent formatting, author PA, production, or publishing support providers can request directory review.'
      : category?.id === 'human-review'
        ? 'Independent human review, proofreading, editing, or language-service providers can request directory review.'
        : 'Independent author service providers can request directory review.';
  const applicationDetails = category?.id === 'author-marketing'
    ? [
      'Business name, website, and primary contact',
      'Marketing services, supported languages, pricing, and timelines',
      'Experience supporting authors publishing translated editions',
    ]
    : category?.id === 'publishing-support'
      ? [
        'Business name, website, and primary contact',
        'Formatting or publishing support services, pricing, and timelines',
        'Language coverage and experience with translated editions',
      ]
      : [
        'Business name, website, and primary contact',
        'Services, languages, pricing, and turnaround times',
        'Experience with AI-assisted translation or ScribeShadow workflows',
      ];

  const resetLogoUpload = () => {
    setLogoFile(null);
    setLogoFileList([]);
  };

  const openApplicationForm = () => {
    setIsModalOpen(true);
    trackDirectoryEvent('human_review_provider_application_opened', category);
  };

  const closeApplicationForm = () => {
    if (!isSubmitting) {
      setIsModalOpen(false);
    }
  };

  const handleLogoBeforeUpload = (file: RcFile) => {
    if (!PROVIDER_LOGO_ACCEPTED_TYPES.includes(file.type)) {
      message.error('Logo must be a PNG, JPG, GIF, WebP, or SVG image.');
      return false;
    }
    if (file.size > PROVIDER_LOGO_MAX_BYTES) {
      message.error('Logo must be smaller than 5 MB.');
      return false;
    }
    setLogoFile(file);
    setLogoFileList([
      {
        uid: file.uid,
        name: file.name,
        status: 'done',
      },
    ]);
    return false;
  };

  const submitApplication = async (values: ProviderApplicationFormValues) => {
    setIsSubmitting(true);
    const serviceLabels = getProviderApplicationServiceLabels(values.serviceTypes);
    const requestedBadgeLabels = getProviderApplicationBadgeLabels(values.requestedBadges);
    const servicesSummary = [
      ...serviceLabels.filter((serviceLabel) => serviceLabel !== 'Other'),
      ...(values.otherService ? [values.otherService] : []),
      ...(values.services ? [values.services] : []),
    ].join('\n');
    const formData = new FormData();
    appendProviderApplicationValue(formData, 'businessName', values.businessName);
    appendProviderApplicationValue(formData, 'websiteUrl', values.websiteUrl);
    appendProviderApplicationValue(formData, 'contactName', values.contactName);
    appendProviderApplicationValue(formData, 'contactEmail', values.contactEmail);
    appendProviderApplicationValues(formData, 'serviceTypes', values.serviceTypes);
    appendProviderApplicationValues(formData, 'serviceLabels', serviceLabels);
    appendProviderApplicationValue(formData, 'otherService', values.otherService);
    appendProviderApplicationValue(formData, 'services', servicesSummary);
    appendProviderApplicationValue(formData, 'languages', values.languages);
    appendProviderApplicationValue(formData, 'pricing', values.pricing);
    appendProviderApplicationValue(formData, 'turnaround', values.turnaround);
    appendProviderApplicationValue(formData, 'workflowExperience', values.workflowExperience);
    appendProviderApplicationValues(formData, 'requestedBadges', values.requestedBadges);
    appendProviderApplicationValues(formData, 'requestedBadgeLabels', requestedBadgeLabels);
    appendProviderApplicationValue(formData, 'discountDetails', values.discountDetails);
    appendProviderApplicationValue(formData, 'referralDetails', values.referralDetails);
    appendProviderApplicationValue(formData, 'educationDetails', values.educationDetails);
    appendProviderApplicationValue(formData, 'notes', values.notes);
    appendProviderApplicationValue(formData, 'categoryId', category?.id);
    appendProviderApplicationValue(formData, 'categoryTitle', category?.title);
    if (logoFile) {
      formData.append('logoFile', logoFile);
    }

    try {
      const response = await fetch(`${API_BASE}/provider-directory/applications`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Provider application failed with ${response.status}`);
      }

      message.success('Listing request received. We will review it and follow up by email.');
      form.resetFields();
      resetLogoUpload();
      setIsModalOpen(false);
      trackDirectoryEvent('human_review_provider_application_submitted', category);
    } catch (error) {
      console.error('Failed to submit provider directory application', error);
      message.error('We could not send the listing request. Please try again.');
      trackDirectoryEvent('human_review_provider_application_failed', category);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="resources-application-card" aria-labelledby="provider-application-title">
        <div>
          <span className="resources-kicker">Provider Listings</span>
          <h2 id="provider-application-title">Apply to be listed</h2>
          <p>{applicationSummary}</p>
        </div>
        <ul className="resources-application-list" aria-label="Information to include">
          {applicationDetails.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
        <Button icon={<FileSearchOutlined />} onClick={openApplicationForm}>
          Request Listing Review
        </Button>
      </section>

      <Modal
        title="Apply to be listed"
        open={isModalOpen}
        onCancel={closeApplicationForm}
        footer={null}
        destroyOnClose
        width={960}
        className="resources-provider-application-modal"
      >
        <div className="resources-provider-application-header">
          <span className="resources-provider-application-icon" aria-hidden="true">
            <FileSearchOutlined />
          </span>
          <p>Share provider details for directory review. Badge requests are reviewed before anything is displayed publicly.</p>
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={submitApplication}
          className="resources-provider-application-form"
        >
          <div className="resources-provider-application-section">
            <span className="resources-provider-application-section-title">Business profile</span>
            <div className="resources-provider-application-grid">
              <Form.Item
                label="Business name"
                name="businessName"
                rules={[{ required: true, message: 'Enter the business name.' }]}
              >
                <Input autoComplete="organization" />
              </Form.Item>
              <Form.Item label="Website" name="websiteUrl">
                <Input autoComplete="url" placeholder="https://example.com" />
              </Form.Item>
              <Form.Item
                label="Contact name"
                name="contactName"
                rules={[{ required: true, message: 'Enter a contact name.' }]}
              >
                <Input autoComplete="name" />
              </Form.Item>
              <Form.Item
                label="Contact email"
                name="contactEmail"
                rules={[
                  { required: true, message: 'Enter a contact email.' },
                  { type: 'email', message: 'Enter a valid email address.' },
                ]}
              >
                <Input autoComplete="email" />
              </Form.Item>
              <Form.Item className="resources-provider-application-logo" label="Preferred logo">
                <Upload
                  accept={PROVIDER_LOGO_ACCEPTED_TYPES.join(',')}
                  beforeUpload={handleLogoBeforeUpload}
                  fileList={logoFileList}
                  maxCount={1}
                  onRemove={() => {
                    resetLogoUpload();
                    return true;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload Logo</Button>
                </Upload>
                <Text type="secondary">Optional. PNG, JPG, GIF, WebP, or SVG under 5 MB.</Text>
              </Form.Item>
            </div>
          </div>

          <div className="resources-provider-application-section">
            <span className="resources-provider-application-section-title">Directory details</span>
            <Form.Item
              label="Service areas"
              name="serviceTypes"
              rules={[{ required: true, message: 'Select at least one service area.' }]}
            >
              <Select
                mode="multiple"
                maxTagCount="responsive"
                placeholder="Select services"
                options={providerApplicationServiceOptions}
              />
            </Form.Item>
            {showOtherService && (
              <Form.Item
                label="Other service"
                name="otherService"
                rules={[{ required: true, message: 'Describe the other service.' }]}
              >
                <Input />
              </Form.Item>
            )}
            <Form.Item
              label="Service notes"
              name="services"
            >
              <Input placeholder="Specialties, manuscript types, or review scope" />
            </Form.Item>
            <div className="resources-provider-application-grid">
              {showLanguageFields && (
                <Form.Item label="Languages covered" name="languages">
                  <Input />
                </Form.Item>
              )}
              <Form.Item label="Typical pricing or rate range" name="pricing">
                <Input />
              </Form.Item>
              <Form.Item label="Typical turnaround time" name="turnaround">
                <Input />
              </Form.Item>
            </div>
          </div>

          <div className="resources-provider-application-section">
            <span className="resources-provider-application-section-title">Badges to review</span>
            <Form.Item
              className="resources-provider-application-badge-select"
              label="Potential directory badges"
              name="requestedBadges"
            >
              <Select
                mode="multiple"
                maxTagCount="responsive"
                placeholder="Select any that apply"
                options={providerApplicationBadgeOptions}
              />
            </Form.Item>
            {(showWorkflowField || showDiscountField || showReferralField || showEducationField) && (
              <div className="resources-provider-application-badge-details">
                {showWorkflowField && (
                  <Form.Item
                    label="ScribeShadow workflow experience"
                    name="workflowExperience"
                  >
                    <Input />
                  </Form.Item>
                )}
                {showDiscountField && (
                  <Form.Item label="User discount details" name="discountDetails">
                    <Input />
                  </Form.Item>
                )}
                {showReferralField && (
                  <Form.Item label="Referral tracking details" name="referralDetails">
                    <Input />
                  </Form.Item>
                )}
                {showEducationField && (
                  <Form.Item label="Education or author support offered" name="educationDetails">
                    <Input />
                  </Form.Item>
                )}
              </div>
            )}
            <Form.Item className="resources-provider-application-notes" label="Additional notes" name="notes">
              <Input />
            </Form.Item>
          </div>
          <div className="resources-provider-application-actions">
            <Button onClick={closeApplicationForm} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Submit Request
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export const ResourcesHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'providers' ? 'providers' : 'guides';
  usePageMetadata(RESOURCES_META_TITLE, RESOURCES_META_DESCRIPTION);
  useEffect(() => { markResourcesSeen(); }, []);

  const changeTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'providers') next.set('tab', key);
    else next.delete('tab');
    setSearchParams(next);
  };

  return (
    <main className="resources-page-shell resources-hub">
      <div className="resources-page-inner">
        <AppPageHeader
          className="resources-hero"
          variant="banner"
          title="Resources"
          subtitle="Explore publishing guides for translated editions and independent provider directories."
          titleId="resources-page-title"
        />
        <Tabs
          className="resources-tabs"
          tabBarGutter={16}
          activeKey={activeTab}
          onChange={changeTab}
          items={[
            { key: 'guides', label: 'Publishing Guides', children: <PublishingGuidesSection /> },
            { key: 'providers', label: 'Provider Directories', children: <ProviderDirectoriesPanel active={activeTab === 'providers'} /> },
          ]}
        />
      </div>
    </main>
  );
};

const ProviderDirectoriesPanel: React.FC<{ active: boolean }> = ({ active }) => {
  const { status, providers, retry } = useProviderDirectoryProviders();
  const activeCategories = useMemo(
    () => (status === 'success' ? getActiveProviderCategories(providers) : []),
    [providers, status],
  );
  const providerCount = activeCategories.reduce(
    (count, category) => count + getProvidersForCategory(category, providers).length,
    0,
  );
  const activeCategoryIds = activeCategories.map((category) => category.id).join('|');
  const [newCategoryIds, setNewCategoryIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!active || status !== 'success') {
      return;
    }
    setNewCategoryIds(new Set(
      activeCategories
        .filter((category) => !hasSeenResourceCategory(category.id))
        .map((category) => category.id),
    ));
    markResourceCategoriesSeen(activeCategoryIds.split('|').filter(Boolean));
  }, [active, activeCategories, activeCategoryIds, status]);

  useEffect(() => {
    if (!active || status !== 'success') {
      return;
    }
    trackDirectoryEvent('human_review_directory_viewed', undefined, undefined, {
      categoryCount: activeCategories.length,
      providerCount,
      view: 'resources_hub',
    });
  }, [active, activeCategories.length, providerCount, status]);

  return (
    <div className="resources-provider-panel">
        {status === 'loading' && <ProviderDirectoryStateCard state="loading" scope="hub" />}
        {status === 'error' && <ProviderDirectoryStateCard state="error" scope="hub" onRetry={retry} />}
        {status === 'success' && activeCategories.length === 0 && (
          <ProviderDirectoryStateCard state="empty" scope="hub" />
        )}
        {status === 'success' && activeCategories.length > 0 && (
          <section className="resources-section-card resources-section-card--directories resources-hub-section" aria-labelledby="resources-provider-directories-title">
            <div className="resources-section-heading">
              <div>
                <span className="resources-kicker">Find publishing support</span>
                <h2 id="resources-provider-directories-title">Provider directories</h2>
                <p>
                  ScribeShadow resource directories list independent third-party businesses that may offer
                  services relevant to author publishing workflows.
                </p>
              </div>
            </div>

            <p className="resources-section-note">
              Directory inclusion is informational and does not create an official provider relationship or
              guarantee of service quality.
            </p>

            <div className="resources-directory-grid" aria-label="Provider directories">
              {activeCategories.map((category) => {
                const categoryProviders = getProvidersForCategory(category, providers);
                return (
                  <Link className="resources-directory-link" to={getProviderCategoryRoute(category)} key={category.id}>
                    <span className="resources-directory-main">
                      <span className="resources-directory-icon" aria-hidden="true">
                        <FileSearchOutlined />
                      </span>
                      <span className="resources-directory-copy">
                        <h3>
                          <span>{category.title}</span>
                          {newCategoryIds.has(category.id) && (
                            <span className="resources-new-indicator" aria-hidden="true">New</span>
                          )}
                        </h3>
                        <small>{category.description}</small>
                      </span>
                    </span>
                    <span className="resources-directory-action">
                      <span className="resources-directory-meta" aria-hidden="true">
                        <span>{categoryProviders.length} provider{categoryProviders.length === 1 ? '' : 's'}</span>
                      </span>
                      <span className="resources-directory-action-label">
                        <ArrowRightOutlined />
                        View Providers
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <ProviderApplicationCard />
    </div>
  );
};

const ProviderCard: React.FC<{
  category: ServiceProviderCategory;
  provider: ServiceProvider;
}> = ({ category, provider }) => {
  const providerInitial = provider.name.trim().charAt(0).toUpperCase();
  const categoryContent = provider.categoryContent?.[category.id];
  const providerSubtitle = categoryContent?.subtitle || provider.subtitle;
  const providerDescription = categoryContent?.description || provider.description;
  const providerServices = categoryContent?.services || provider.services;
  const logoFit = provider.logoFit || 'contain';
  const logoInset = provider.logoInset || 'none';

  return (
    <article className="resources-provider-card">
      <div className="resources-provider-card-main">
        <div className="resources-provider-identity">
          {provider.logoSrc ? (
            <span className={`resources-provider-logo-frame resources-provider-logo-frame--${provider.logoTheme || 'dark'} resources-provider-logo-frame--fit-${logoFit} resources-provider-logo-frame--inset-${logoInset}`}>
              <img src={provider.logoSrc} alt={provider.logoAlt || `${provider.name} logo`} />
            </span>
          ) : (
            <span className="resources-provider-avatar" aria-hidden="true">{providerInitial}</span>
          )}
          <div>
            <span className="resources-kicker">Independent Provider</span>
            <h2>{provider.name}</h2>
            <p>{providerSubtitle}</p>
          </div>
        </div>

        <p className="resources-provider-description">{providerDescription}</p>

        <div className="resources-provider-section">
          <div className="resources-provider-section-title">
            <TagsOutlined />
            <span>Informational badges</span>
          </div>
          <div className="resources-provider-badges">
            {provider.badges.map((badge) => {
              const definition = providerBadgeDefinitions[badge];
              return (
                <Tooltip title={definition.description} key={badge}>
                  <Tag className="resources-provider-badge">
                    {definition.label}
                    <InfoCircleOutlined />
                  </Tag>
                </Tooltip>
              );
            })}
          </div>
        </div>

        <div className="resources-provider-section">
          <div className="resources-provider-section-title">
            <CheckCircleOutlined />
            <span>Services</span>
          </div>
          <ul className="resources-provider-services">
            {providerServices.map((service) => (
              <li key={service}>{service}</li>
            ))}
          </ul>
        </div>
      </div>

      <aside className="resources-provider-sidebar" aria-label={`${provider.name} contact and offer details`}>
        {provider.contactEmail && (
          <Button
            type="primary"
            icon={<MailOutlined />}
            href={getEmailHref(provider.contactEmail)}
            onClick={() => trackDirectoryEvent('human_review_provider_contact_clicked', category, provider)}
            block
          >
            Contact Provider
          </Button>
        )}

        {provider.websiteUrl && (
          <Button
            href={provider.websiteUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackDirectoryEvent('human_review_provider_website_clicked', category, provider)}
            block
          >
            Visit Website
          </Button>
        )}

        {provider.discount && (
          <div className="resources-provider-detail">
            <Text strong>Discount</Text>
            <p>{provider.discount}</p>
          </div>
        )}
        {(provider.contactName || provider.contactEmail || provider.websiteUrl) && (
          <div className="resources-provider-detail">
            <Text strong>Contact</Text>
            {provider.contactName && <p>{provider.contactName}</p>}
            {provider.contactEmail && (
              <a
                href={getEmailHref(provider.contactEmail)}
                onClick={() => trackDirectoryEvent('human_review_provider_contact_clicked', category, provider)}
              >
                {provider.contactEmail}
              </a>
            )}
            {provider.phone && <p>{provider.phone}</p>}
            {provider.websiteUrl && (
              <a
                href={provider.websiteUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackDirectoryEvent('human_review_provider_website_clicked', category, provider)}
              >
                {provider.websiteUrl}
              </a>
            )}
          </div>
        )}
      </aside>

      <div className="resources-provider-disclaimer">
        {provider.disclaimer}
      </div>
    </article>
  );
};

const UnknownCategory: React.FC = () => {
  usePageMetadata('Provider Directory Not Found | ScribeShadow', RESOURCES_META_DESCRIPTION);

  return (
    <main className="resources-page-shell">
      <div className="resources-page-inner">
        <AppPageHeader
          className="resources-hero"
          variant="banner"
          title="Provider directory not found"
          subtitle="This provider directory is not available. Browse active resource directories instead."
          actions={
            <Button type="link" className="resources-hero-link" icon={<FileSearchOutlined />}>
              <Link to={RESOURCES_ROUTE}>Open Resources</Link>
            </Button>
          }
          titleId="resources-not-found-title"
        />
      </div>
    </main>
  );
};

export const ProviderCategoryDirectory: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = getProviderCategoryBySlug(categorySlug);

  if (!category) {
    return <UnknownCategory />;
  }

  return <ProviderCategoryDirectoryContent category={category} />;
};

const ProviderCategoryDirectoryContent: React.FC<{
  category: ServiceProviderCategory;
}> = ({ category }) => {
  const { status, providers: allProviders, retry } = useProviderDirectoryProviders();
  const providers = useMemo(
    () => (status === 'success' ? getProvidersForCategory(category, allProviders) : []),
    [allProviders, category, status],
  );
  const randomizedProviders = useMemo(() => shuffleProviders(providers), [providers]);

  usePageMetadata(category.metaTitle, category.metaDescription);

  useEffect(() => {
    if (status !== 'success') {
      return;
    }
    markResourcesSeen();
    markResourceCategorySeen(category.id);
    trackDirectoryEvent('human_review_directory_viewed', category, undefined, {
      providerCount: providers.length,
      view: 'provider_category',
    });
  }, [category, providers.length, status]);

  return (
    <main className="resources-page-shell">
      <div className="resources-page-inner">
        <AppPageHeader
          className="resources-hero"
          variant="banner"
          title={category.title}
          subtitle={category.description}
          actions={
            <Button type="link" className="resources-hero-link" icon={<ArrowLeftOutlined />}>
              <Link to={`${RESOURCES_ROUTE}?tab=providers`}>Resources</Link>
            </Button>
          }
          titleId="provider-directory-title"
        />

        <section className="resources-section-card" aria-labelledby="provider-directory-info-title">
          <div className="resources-section-heading">
            <div>
              <span className="resources-kicker">Directory Information</span>
              <h2 id="provider-directory-info-title">Before contacting a provider</h2>
              <p>
                Providers listed here are independent third-party businesses. ScribeShadow does not control
                their pricing, availability, timelines, service quality, refunds, or disputes.
              </p>
              <p>
                Contact providers directly to discuss pricing, timelines, language coverage, and service details.
              </p>
            </div>
          </div>
        </section>

        {status === 'loading' && <ProviderDirectoryStateCard state="loading" scope="category" />}
        {status === 'error' && <ProviderDirectoryStateCard state="error" scope="category" onRetry={retry} />}
        {status === 'success' && randomizedProviders.length === 0 && (
          <ProviderDirectoryStateCard state="empty" scope="category" />
        )}
        {status === 'success' && randomizedProviders.length > 0 && (
          <>
            <section className="resources-provider-list" aria-label={category.title}>
              {randomizedProviders.map((provider) => (
                <ProviderCard category={category} provider={provider} key={provider.id} />
              ))}
            </section>

            <section className="resources-section-card resources-directory-note" aria-label="Provider directory note">
              <strong>Directory note:</strong> Badges are informational labels, not rankings, endorsements,
              guarantees, certifications, or quality ratings from ScribeShadow.
            </section>
          </>
        )}

        <ProviderApplicationCard category={category} />
      </div>
    </main>
  );
};
